import { appState } from '../../interfaces/common';
import { Button, Flex, Grid, Icon, Label, TextArea, Tooltip, useElement } from '@pega/cosmos-react-core';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import useLocalTranslation from '../../hooks/useLocalTranslation';
import { updateBannerInfo, updateLoaderDetails } from '../../redux/actions';
import Message from '../cosmos-social/Message';
import { updateMenuConfig } from '../SocialMedia/SMS/SMSApis';
import { updateMenuReplyConfiguration } from '../SocialMedia/TwilioWhatsapp/TwilioWhatsappApis';
import { StyledCardMessage } from './custom.styled';

interface TwilioFormData {
  menuConfiguration: string;
}

interface TwilioProps {
  menuReplyConfiguration?: string;
  connectionId: string;
  source: 'sms' | 'whatsapp';
  hasTwilioWhatsAppTemplate?: boolean;
  messagingServiceSid?: string;
}

const MenuReplyConfiguration: React.FC<TwilioProps> = ({
  menuReplyConfiguration,
  source,
  connectionId,
  hasTwilioWhatsAppTemplate,
  messagingServiceSid,
}) => {
  const { register, errors, formState } = useForm<TwilioFormData>({ mode: 'all' });
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const navigate = useNavigate();

  const [toolTip, setToolTip] = useElement();
  const [toolTipPreview, setToolTipPreview] = useElement();
  const defaultMenuConfig = '{label} (TXT: {#})';
  const [menuConfiguration, setMenuConfiguration] = useState(
    menuReplyConfiguration ? menuReplyConfiguration : defaultMenuConfig,
  );
  const sessionId = useSelector((state: appState) => state.sessionId);
  const labelReplacement = 'I have a billing question';

  const configValidation = (value: string) => {
    setMenuConfiguration(value);

    const menuConfigLowerCase = value.toLocaleLowerCase();
    const labelCount = (menuConfigLowerCase.match(/{label}/g) || []).length;
    const indexCount = (menuConfigLowerCase.match(/{#}/g) || []).length;
    let validationMsg: string = '';

    if (labelCount <= 0) {
      validationMsg = translate('menu_configuration_validation');
    } else if (labelCount > 1 && indexCount > 1) {
      validationMsg = translate('menu_configuration_validation_3');
    } else if (labelCount > 1) {
      validationMsg = translate('menu_configuration_validation_2');
    } else if (indexCount > 1) {
      validationMsg = translate('menu_configuration_validation_4');
    }

    return validationMsg ? validationMsg : true;
  };
  const updateMenuConfiguration = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMenuConfiguration(event?.target?.value);
  };
  const saveMenuConfiguration = async (updateMenu?: string) => {
    dispatch(updateLoaderDetails(true));
    const updatedMenuConfig = updateMenu === 'reset' ? defaultMenuConfig : menuConfiguration;
    const json =
      source === 'sms'
        ? await updateMenuConfig(sessionId, connectionId, updatedMenuConfig)
        : await updateMenuReplyConfiguration(
            sessionId,
            connectionId,
            updatedMenuConfig,
            hasTwilioWhatsAppTemplate,
            messagingServiceSid,
          );
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(updateBannerInfo(`${translate('menu_config_update_success')}`, 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      let errorMessage;
      if (json?.data?.code) {
        errorMessage = translate(json.data.code);
      }
      dispatch(updateBannerInfo(errorMessage || translate('menu_config_update_failure'), 'urgent'));
    }
  };

  const resetMenuConfiguration = () => {
    setMenuConfiguration(defaultMenuConfig);
    saveMenuConfiguration('reset');
  };

  const LeftAlignTooltip = { textAlign: 'left' };
  return (
    <>
      <Grid
        container={{
          cols: 'repeat(3, 30% 52% 8%)',
          gap: 3,
          alignItems: 'center',
        }}
      >
        <Label>
          {translate('menu_reply_config')} <span style={{ color: '#d91c29' }}> *</span>
          <Button variant="simple" style={{ width: 15, height: 15 }} icon ref={setToolTip}>
            <Icon name={'help'} />
            <Tooltip target={toolTip} style={LeftAlignTooltip}>
              {translate('menu_config_note')}
            </Tooltip>
          </Button>
        </Label>
        <Flex container={{ direction: 'column', itemGap: 1, alignItems: 'end' }}>
          <Button
            variant="link"
            style={{ textAlign: 'end' }}
            onClick={resetMenuConfiguration}
            disabled={menuConfiguration.trim() === defaultMenuConfig}
          >
            {translate('reset_menu')}
          </Button>
          <Flex style={{ width: '100%' }}>
            <TextArea
              name="menuConfiguration"
              label="menuConfiguration"
              labelHidden={true}
              value={menuConfiguration}
              onChange={updateMenuConfiguration}
              info={errors.menuConfiguration?.message}
              required={true}
              status={errors.menuConfiguration?.message ? 'error' : undefined}
              ref={register({
                required: translate('menu_configuration_empty'),
                validate: configValidation,
              })}
            />
          </Flex>
        </Flex>
        <Button
          variant="primary"
          style={{ marginTop: '20px' }}
          disabled={!formState.isValid}
          onClick={saveMenuConfiguration}
        >
          {translate('save')}
        </Button>
      </Grid>
      <Grid
        container={{
          cols: 'repeat(2, 30% 52% )',
          gap: 3,
          alignItems: 'center',
        }}
      >
        <Label>
          {translate('menu_preview')}
          <Button variant="simple" style={{ width: 15, height: 15 }} icon ref={setToolTipPreview}>
            <Icon name={'help'} />
            <Tooltip target={toolTipPreview} style={LeftAlignTooltip}>
              {translate('menu_preview_note')}
            </Tooltip>
          </Button>
        </Label>
        <StyledCardMessage style={{ borderRadius: 0 }}>
          <Message
            variant={'sender'}
            direction={'in'}
            message={menuConfiguration.replace(/{label}/gi, labelReplacement).replace('{#}', '1')}
          />
        </StyledCardMessage>
      </Grid>
    </>
  );
};

export default MenuReplyConfiguration;
